/**
 * Pagina "Wishlist" — candidati all'acquisto (SPEC-012.2 §4.2).
 *
 * Le righe si creano dalla watchlist, dove il titolo c'è già: qui non c'è un
 * pulsante "nuovo". Una creazione da questa pagina richiederebbe un selettore di
 * titolo che duplicherebbe la ricerca già presente in watchlist, e la
 * precondizione (§2.2) resterebbe comunque "il titolo deve essere in watchlist" —
 * cioè l'utente verrebbe rimandato lì lo stesso. Lo stato vuoto lo dice.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { Alert, Card, EmptyState, Spinner, Switch } from '@/ui';
import { useWishlist, type WishlistItem } from './api';
import { WishlistItemModal } from './WishlistItemModal';
import { WishlistRevivalCard } from './WishlistRevivalCard';
import { WishlistTable } from './WishlistTable';
import './wishlist.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

export function WishlistPage() {
  const [includeExpired, setIncludeExpired] = useState(false);
  const [editing, setEditing] = useState<WishlistItem | null>(null);
  const wishlist = useWishlist(null, includeExpired);

  const items = wishlist.data?.items ?? [];
  const expiredCount = items.filter((item) => item.expired).length;

  return (
    <div className="wishlist">
      <header className="page-header">
        <div>
          <h1>Wishlist</h1>
          <p className="page-header__subtitle">
            I titoli che compreresti, portafoglio per portafoglio, entro i limiti che hai fissato.
            Una riga vale per la coppia titolo + portafoglio: lo stesso titolo può avere vincoli
            diversi su conti diversi.
          </p>
        </div>
        <Switch
          label="Mostra anche i candidati scaduti"
          checked={includeExpired}
          onChange={setIncludeExpired}
        />
      </header>

      <WishlistRevivalCard />

      {wishlist.isLoading && <Spinner label="Caricamento della wishlist…" />}

      {wishlist.isError && (
        <Alert variant="error" title="Wishlist non disponibile">
          {errorMessage(wishlist.error, 'Errore imprevisto')}
        </Alert>
      )}

      {wishlist.data && items.length === 0 && (
        <EmptyState
          icon="🎯"
          title={includeExpired ? 'Nessun candidato, nemmeno scaduto' : 'Nessun candidato all’acquisto'}
          description="I candidati si aggiungono dalla watchlist, dalla riga del titolo: è lì che il titolo esiste già, e da lì scegli portafoglio, limiti e scadenza."
          action={
            <Link className="btn btn--primary" to="/watchlists">
              Vai alla watchlist
            </Link>
          }
        />
      )}

      {wishlist.data && items.length > 0 && (
        <>
          {includeExpired && expiredCount > 0 && (
            <Alert variant="info" title="Alcuni candidati sono scaduti">
              {expiredCount === 1
                ? 'Un candidato è scaduto e non compare negli elenchi normali.'
                : `${expiredCount} candidati sono scaduti e non compaiono negli elenchi normali.`}{' '}
              Non sono stati cancellati: «Rinnova» li riporta a {wishlist.data.defaultExpiryDays} giorni.
            </Alert>
          )}
          <Card flush>
            <WishlistTable items={items} onEdit={setEditing} />
          </Card>
        </>
      )}

      {editing && (
        <WishlistItemModal
          item={editing}
          defaultExpiryDays={wishlist.data?.defaultExpiryDays ?? 7}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
